/**
 * useTodoDeleteMutation — Mutation hook for deleting a todo item.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { logger } from '@/utils/logger';

export const useTodoDeleteMutation = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  
  const deleteMutation = useMutation({
    mutationFn: async ({ todoId }: { todoId: string }) => {
      if (!user) throw new Error("User must be authenticated");
      
      logger.debug(`[TodoDeleteMutation] Deleting todo with ID: ${todoId}`);
      
      // Simplified query - no need to check user_id as RLS will handle this
      const { error } = await supabase
        .from('todos')
        .delete()
        .eq('id', todoId);
        
      if (error) {
        logger.error(`[TodoDeleteMutation] Error deleting todo:`, error);
        throw error;
      }
      
      return { success: true, todoId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
    },
    onError: (error: Error) => {
      handleError(error, { title: "Error deleting todo" });
    }
  });

  return {
    deleteTodo: (params: { todoId: string }) => deleteMutation.mutateAsync(params),
    isDeleting: deleteMutation.isPending
  };
};
